import useSigninUser from "@QUERIES/account/useSigninUser";
import {useQueryClient} from "@tanstack/react-query";
import useConfigureAxios from "@FRAMEWORK/utils/useConfigureAxios";
import publicRuntimeConfig from "@FRAMEWORK/config/public";

interface WithWishListProps {
	/** wish 여부
	 * @description default false
	 */
	isActive?: boolean;

	/** 위시리시트 구분값 ( 필수 )
	 * @description 상품 건 인지 행사 건 인지
	 * @description G 상품 E 행사
	 */
	diviCd: "G" | "E";

	/** 상품코드
	 * @description evCd, goodsCd중 하나가 없을 경우 오류 메시지 처리함
	 * @description default ""
	 */
	// goodsCd?: string;

	/** 행사코드
	 * @description evCd, goodsCd중 하나가 없을 경우 오류 메시지 처리함
	 * @description default ""
	 */
	evCd?: string;

	/** url 파라미터
	 * @example /product/detailPackage.m?menu=PKG&dspSid=AABQ000&goodsCd=ATP6131
	 * @description 해당 위시 건에 대한 URL
	 * @description default ""
	 */
	link?: string;

	/** 위시리스트 키 wishNo
	 * @description 해당 위시 건에 대한 취소 처리를 위한 값
	 * @description default ""
	 */
	wishNo?: string;

	/** 위시 등록 - 확인 callback
	 * @description () => void
	 */
	addConfirm?: (e: any) => void;

	/** 위시 등록 - 취소 callback
	 * @description () => void
	 * @deprecated
	 */
	addCancel?: () => void;

	/** 위시 취소 - 확인 callback
	 * @description () => void
	 */
	removeConfirm?: (e: any) => void;

	/** 위시 취소 - 취소 callback
	 * @description () => void
	 * @deprecated
	 */
	removeCancel?: () => void;
}

const withWishList = WrappedComponent => (props: WithWishListProps) => {
	const {isActive = false, evCd = "", link = "", diviCd, wishNo = "", addConfirm, removeConfirm} = props;
	const goodsCd = evCd.split("-")[0];
	const goodsTp = evCd.substring(0, 1);

	/** 로그인 */
	const queryClient = useQueryClient();

	/** 로그인 */
	const signin = useSigninUser();
	const webCustNo = signin.data?.body.webCustNo || "";
	const signinCode = signin.data?.code || "9999";

	const addWish = async () => {
		const result = await useConfigureAxios({
			url: `${publicRuntimeConfig.ENV_ESSENTIAL_API_BASEPATH}/api/customer/wishList`,
			method: "POST",
			data: {
				webCustNo,
				goodsCd,
				goodsNm: "",
				evCd,
				diviCd,
				imageThum: "",
				link: `${publicRuntimeConfig.ENV_PRODUCT_URL}${link}`,
				itemCategoryCode: `${goodsTp === "K" ? "D" : "P"}`,
				cityCode: "",
				latestAmount: "",
			},
		}).catch(e => {
			throw new Error();
		});
		if (result.data.code === "0000") {
			MessageUtil.alert("찜한 상품으로 등록 되었습니다.");
			queryClient.refetchQueries({queryKey: ["useWishlist"], type: "active"});
		} else {
			throw new Error();
		}
	};

	const removeWish = async () => {
		if (!wishNo) return;
		const result = await useConfigureAxios({
			url: `${publicRuntimeConfig.ENV_ESSENTIAL_API_BASEPATH}/api/customer/wishList`,
			method: "DELETE",
			data: {wishList: [{productWishId: wishNo, webCustNo, evCd, goodsCd, itemCategoryCode: "P"}]},
		}).catch(e => {
			throw new Error();
		});
		if (result.data.code === "0000") {
			MessageUtil.alert("찜한 상품이 삭제 되었습니다.");
			queryClient.refetchQueries({queryKey: ["useWishlist"], type: "active"});
		} else {
			throw new Error();
		}
	};

	const wish = async e => {
		if (!isActive) {
			/** 위시리스트 등록 */
			const isConfirm = await MessageUtil.confirm("찜한 상품으로 등록하시겠습니까?");
			if (isConfirm) {
				addWish();
				typeof addConfirm === "function" && addConfirm(e);
			}
		} else {
			/** 위시리스트 삭제 */
			const isConfirm = await MessageUtil.confirm("찜한 상품을 삭제하시겠습니까?");
			if (isConfirm) {
				removeWish();
				typeof removeConfirm === "function" && removeConfirm(e);
			}
		}
	};

	const onClick = async e => {
		try {
			if (!evCd) throw new Error("필수 파라미터 누락");
			if (signinCode !== "0000") {
				MessageUtil.confirm("로그인 후 이용하실 수 있습니다. <br/>로그인하시겠습니까?", () => LoginUtil.login());
			} else {
				wish(e);
			}
		} catch (e) {
			console.log(e);
			MessageUtil.alert(`오류가 발생하였습니다. <br/>${e}`);
		}
	};

	return (
		<>
			<WrappedComponent onClick={onClick} isActive={props.isActive} />
		</>
	);
};

export default withWishList;
